import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { sortPlansByCategory } from '@/helpers';
import { Button, ChecksPriceEntry, DropdownPanel, SvgIcon } from '@/components';

import { Plan } from '@/api';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { paymentActions } from '@/redux/actions';
import { profileSelectors, userSelectors } from '@/redux/selectors';
import {
  getPlansAvailability,
  navigationPages,
  PlanCategoryEnum,
} from '@/config';

import styles from './BalanceInfo.module.sass';

interface BalanceInfoProps {
  isActive: boolean;
  balance: number;
  plans: Plan[] | null;
  isUserLocked: boolean;
  isPromo?: boolean;
  onToggle: (isActive: boolean) => void;
}

const BalanceInfo: FC<BalanceInfoProps> = (props) => {
  const { isActive, balance, plans, isUserLocked, isPromo, onToggle } = props;

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [selectedEntry, setSelectedEntry] = useState<Plan | null>(null);
  const [activeCategory, setActiveCategory] = useState<PlanCategoryEnum | null>(
    null,
  );
  // Derived during render, not mirrored into state via an effect: an effect
  // lags the `plans` prop by one render, and during that render the categories
  // object was still empty while `plans` was already populated. Deriving it
  // keeps the two in sync, and sortPlansByCategory always seeds every category
  // key, so planCategories[activeCategory] is never undefined.
  const planCategories = useMemo(
    () => sortPlansByCategory(plans ?? []),
    [plans],
  );
  const { data: profileData } = useAppSelector(
    profileSelectors.getProfileSlice,
  );
  const isAiTraceOnly = useAppSelector(userSelectors.checkIfAiTraceOnly);

  useEffect(() => {
    dispatch(paymentActions.fetchPriceListPlans());
  }, []);

  useEffect(() => {
    if (profileData?.accountMode) {
      const initialCategory = getPlansAvailability(
        profileData.accountMode,
        isAiTraceOnly,
      ).available[0];
      setActiveCategory(initialCategory);
    }
  }, [profileData?.accountMode, isAiTraceOnly]);

  const hasPlansInCategory = !!(
    activeCategory && planCategories[activeCategory]?.length
  );

  const handleOpenDropdown = useCallback(() => {
    if (isUserLocked) return;
    if (!hasPlansInCategory) return;

    onToggle(true);
  }, [isUserLocked, hasPlansInCategory, onToggle]);

  const goToTariffs = () => {
    onToggle(false);
    navigate(navigationPages.TARIFFS.path, {
      state: { planId: selectedEntry?.id },
    });
  };

  return (
    <div className={styles.wrap}>
      <button
        type="button"
        onClick={handleOpenDropdown}
        disabled={!hasPlansInCategory}
        className={styles.button}
      >
        <SvgIcon name="aml" />
        <span className={styles.text}>{balance}</span>

        {isPromo && (
          <span className={styles.free}>
            | {t('translation.btn_free_check')}
          </span>
        )}
      </button>

      <DropdownPanel
        isActive={isActive}
        onClose={() => onToggle(false)}
        className={styles.panel}
      >
        <div className={styles.title}>
          {t('translation.buy_checks')}
          {/* {activeCategory ? tariffDisplayData[activeCategory].title : ''} */}
        </div>

        {activeCategory && hasPlansInCategory && (
          <div className={styles.list}>
            {planCategories[activeCategory].map((entry) => {
              return (
                <ChecksPriceEntry
                  key={entry.id}
                  isActive={entry === selectedEntry}
                  item={entry}
                  onClick={() => setSelectedEntry(entry)}
                />
              );
            })}
          </div>
        )}

        <Button
          size="m"
          disabled={!selectedEntry}
          onClick={goToTariffs}
          fullwidth
        >
          {t('translation.buy_checks')}
        </Button>

        {/* <div className={styles.discover}>
          <Link to={navigationPages.TARIFFS.path}>
            <span>{t('translation.discover_more_advantageous_plans')}</span>
            <SvgIcon name="arrow-right" />
          </Link>
        </div> */}

        <button
          type="button"
          className={styles.close}
          onClick={() => onToggle(false)}
        >
          <SvgIcon name="close" />
        </button>
      </DropdownPanel>
    </div>
  );
};

export default BalanceInfo;
